import { AutoCustomCompleteField, CustomDateControl, CustomSelect, CustomTimefield, FormField, FormGrid, FormShell, MessageAlert, RBButton, RbToolbarActionButton } from '@scmlatam/web-ui';
import { Icon } from '@mui/material';
import type { useFormUpdateShift } from '../hooks/useFormUpdateShift';
import type { ShiftSegmentType } from '../types/services';

interface UpdateShiftDialogProps {
    formProps: ReturnType<typeof useFormUpdateShift>;
}

const UpdateShiftDialog = ({ formProps }: UpdateShiftDialogProps) => {
    const { formState, setFormState, t } = formProps;

    const handleCancel = () => {
        if (formProps.hasChanges()) {
            formProps.setShowMessage(true);
        } else {
            formProps.closeForm();
        }
    };

    return (
        <FormShell
            open={formProps.openForm}
            title={formProps.title}
            size="m"
            onClose={handleCancel}
            loading={formProps.loading}
            alert={
                formProps.validationMessage ? (
                    <div id="schedule-update-shift-alert-validation">
                        <MessageAlert
                            type="warning"
                            title={formProps.validationMessage}
                            message=""
                            className="rb-message-alert--form"
                            actions={[
                                {
                                    label: 'OK',
                                    onClick: () => formProps.setValidationMessage(''),
                                },
                            ]}
                        />
                    </div>
                ) : formProps.showMessage ? (
                    <div id="schedule-update-shift-alert-unsaved">
                        <MessageAlert
                            type="warning"
                            title={t('edit_shift.alerts.unsaved_changes.title')}
                            message={t('edit_shift.alerts.unsaved_changes.message')}
                            className="rb-message-alert--form"
                            actions={[
                                {
                                    label: t('edit_shift.alerts.unsaved_changes.action_button'),
                                    onClick: () => formProps.setShowMessage(false),
                                },
                                {
                                    label: t('edit_shift.alerts.unsaved_changes.cancel_button'),
                                    onClick: () => {
                                        formProps.setShowMessage(false);
                                        formProps.closeForm();
                                    },
                                },
                            ]}
                        />
                    </div>
                ) : undefined
            }
            footer={
                <div id="schedule-update-shift-footer" className="flex flex-wrap items-center justify-end gap-3 p-3">
                    <RBButton id="schedule-update-shift-cancel" variant="secondary" size="m" disabled={formProps.loading} onClick={handleCancel}>
                        {t('edit_shift.buttons.cancel')}
                    </RBButton>
                    <RBButton id="schedule-update-shift-save" size="m" loading={formProps.loading} onClick={formProps.submit}>
                        {t('edit_shift.buttons.edit')}
                    </RBButton>
                </div>
            }
        >
            <FormGrid id="schedule-update-shift-form">
                <FormField span={12}>
                    <AutoCustomCompleteField
                        id="schedule-update-shift-worker"
                        label={t('edit_shift.fields.worker')}
                        placeholder={t('edit_shift.placeholder.select')}
                        values={formProps.employees}
                        value={formState.employeeId}
                        setValue={(employeeId: string) => setFormState({ ...formState, employeeId })}
                        selectionMode="single"
                        disabled
                        white
                        search={{
                            value: formProps.searchEmployee,
                            onChange: formProps.setSearchEmployee,
                            placeholder: t('edit_shift.placeholder.select'),
                            mode: 'local',
                            emptyText: t('edit_shift.placeholder.select'),
                        }}
                    />
                </FormField>

                <FormField id="schedule-update-shift-date" span={12}>
                    <CustomDateControl
                        label={t('edit_shift.fields.date')}
                        initialValue={null}
                        value={formState.date}
                        setValue={(date) => setFormState({ ...formState, date })}
                        disabled={false}
                        white
                        fullWidth
                    />
                </FormField>

                <FormField span={12}>
                    <div className="overflow-hidden rounded-lg border" style={{ borderColor: '#CECDCA' }}>
                        <table id="schedule-update-shift-segments" className="w-full border-collapse">
                            <thead className="border-b" style={{ borderColor: '#CECDCA' }}>
                                <tr>
                                    <th className="w-12" />
                                    <th className="p-3 text-left font-semibold">{t('edit_shift.fields.start_time')}</th>
                                    <th className="p-3 text-left font-semibold">{t('edit_shift.fields.end_time')}</th>
                                    <th className="p-3 text-left font-semibold">{t('edit_shift.fields.type')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {formState.segments.map((segment) => (
                                    <tr id={`schedule-update-shift-segment-row-${segment.id}`} key={segment.id}>
                                        <td className="p-2 text-center">
                                            <RbToolbarActionButton
                                                id={`schedule-update-shift-segment-remove-${segment.id}`}
                                                aria-label="Remove segment"
                                                variant="icon"
                                                disabled={formState.segments.length === 1}
                                                onClick={() => formProps.removeSegment(segment.id)}
                                                icon={
                                                    <Icon baseClassName="material-symbols-rounded" sx={{ color: '#190007', fontSize: '20px' }}>
                                                        remove_circle
                                                    </Icon>
                                                }
                                            />
                                        </td>
                                        <td id={`schedule-update-shift-segment-start-${segment.id}`} className="p-2">
                                            <CustomTimefield
                                                label=""
                                                initialValue={null}
                                                value={segment.start}
                                                setValue={(start) => formProps.updateSegment(segment.id, { start })}
                                                disabled={false}
                                                white
                                                fullWidth
                                            />
                                        </td>
                                        <td id={`schedule-update-shift-segment-end-${segment.id}`} className="p-2">
                                            <CustomTimefield
                                                label=""
                                                initialValue={null}
                                                value={segment.end}
                                                setValue={(end) => formProps.updateSegment(segment.id, { end })}
                                                disabled={false}
                                                white
                                                fullWidth
                                            />
                                        </td>
                                        <td id={`schedule-update-shift-segment-type-${segment.id}`} className="p-2">
                                            <CustomSelect
                                                label=""
                                                value={segment.type}
                                                setValue={(type: ShiftSegmentType) => formProps.updateSegment(segment.id, { type })}
                                                values={[
                                                    { label: t('edit_shift.fields.regular'), value: 'REGULAR' },
                                                    { label: t('edit_shift.fields.break'), value: 'BREAK' },
                                                ]}
                                                placeholder={t('edit_shift.placeholder.select')}
                                                disabled={false}
                                                white
                                                fullWidth
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="p-2">
                            <RBButton
                                id="schedule-update-shift-add-segment"
                                variant="secondary"
                                size="s"
                                rightIcon={<Icon baseClassName="material-symbols-rounded">add</Icon>}
                                onClick={formProps.addSegment}
                            >
                                {t('edit_shift.buttons.add_row')}
                            </RBButton>
                        </div>
                    </div>
                </FormField>
            </FormGrid>
        </FormShell>
    );
};

export { UpdateShiftDialog };
